import React, { ReactElement, ReactNode } from 'react';

import PropTypes from 'prop-types';

import { parseDate } from '@adsk/alloy-react-datetime';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-helpers';
import theme from '@adsk/alloy-react-theme';

import { User, userShape } from './consts';
import Footer, { FooterProps } from './layout/Footer';
import Header, { HeaderProps } from './layout/Header';

type DateType = Date | string | number;

type ActivityCardProps = Omit<StylableComponent<HTMLDivElement>, 'title'> & {
  title?: ReactNode;
  user?: User;
  date?: DateType;
  footer?: ReactNode;
  renderHeader?: (props: HeaderProps) => ReactElement | null;
  renderFooter?: (props: FooterProps) => ReactElement | null;
  isFirst?: boolean;
  isLast?: boolean;
};

function normalizeDate(date: DateType | undefined): Date | undefined {
  return date ? (date instanceof Date ? date : parseDate(date)) : undefined;
}

/**
 * ActivityCard component. Describes a single activity update event.
 */
const ActivityCard = React.forwardRef<HTMLDivElement, ActivityCardProps>(
  (
    {
      className,
      style,
      title,
      user,
      date,
      isFirst,
      isLast,
      // WPLT-1319
      // eslint-disable-next-line @adsk/react-render-props/render-props-as-components
      renderHeader: HeaderComponent = Header,
      // WPLT-1319
      // eslint-disable-next-line @adsk/react-render-props/render-props-as-components
      renderFooter: FooterComponent = Footer,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={className}
        css={[
          {
            display: 'flex',
            backgroundColor: theme.colors.white,
            width: '100%',
          },
          style,
        ]}
        {...props}
      >
        <div
          css={[
            {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              paddingLeft: 12,
            },
          ]}
        >
          {
            <div
              css={[
                {
                  height: 23,
                  width: 1,
                  backgroundColor: isFirst
                    ? 'transparent'
                    : theme.colors.charcoal200,
                  marginRight: 1,
                },
              ]}
            />
          }
          {
            <div
              css={[
                {
                  borderRadius: '100%',
                  border: `2px solid ${theme.colors.charcoal400}`,
                  width: 8,
                  height: 8,
                },
              ]}
            />
          }
          {!isLast && (
            <div
              css={[
                {
                  flex: 1,
                  width: 1,
                  backgroundColor: theme.colors.charcoal200,
                  marginRight: 1,
                },
              ]}
            />
          )}
        </div>
        <div
          css={[
            {
              display: 'flex',
              flexDirection: 'column',
              padding: '16px 12px',
              overflow: 'auto',
              flex: 1,
            },
          ]}
        >
          <HeaderComponent>{title}</HeaderComponent>
          {children}
          <FooterComponent
            user={user}
            date={normalizeDate(date)}
            style={{ marginTop: children ? 12 : undefined }}
          />
        </div>
      </div>
    );
  }
);

ActivityCard.displayName = 'ActivityCard';

ActivityCard.defaultProps = {
  isFirst: true,
  isLast: true,
};

ActivityCard.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The card title */
  title: PropTypes.node,
  /** The user to show the activity for */
  user: userShape,
  /** The date to show the activity for */
  date: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.string,
    PropTypes.number,
  ]),
  /** Whether this card is the first in activities list */
  isFirst: PropTypes.bool,
  /** Whether this card is the last in activities list */
  isLast: PropTypes.bool,
  /** Render custom header content. Props: ({ title }) */
  renderHeader: PropTypes.func,
  /** Render custom footer content */
  renderFooter: PropTypes.func,
  /** The card content */
  children: PropTypes.node,
};

export default Object.assign(ActivityCard, {
  Header,
  Footer,
});
